<template>
  <div>
    <Container
      :class="$style.container"
    >
      <ItemListPagination
        :columns="columns"
        :currentPageNum="currentPageNum"
        :isLoading="isListLoading"
        :itemComponent="$options.CommandTemplateManageRow"
        :rowsPerPage="rowsPerPage"
        :sortBy="sortBy"
        :sortDirection="sortDirection"
        :tableData="getCollection"
        :totalCount="totalCount[0]"
        @pageChange="handlePageChange"
        @sortColumn="handleSortColumn"
      >
        <template v-slot:toolbar>
          <Grid
            :class="$style.toolbar"
            hasGap
          >
            <GridItem :gridSpan="3">
              <InputSearch
                :focus="focusSearch"
                placeholder="Search"
                :value="searchTerm"
                @input="handleEnterSearchTerm"
              />
            </GridItem>
            <GridItem :gridSpan="5" />
            <GridItem
              :class="$style.filterLabel"
              :gridSpan="2"
            >
              Filter by
            </GridItem>
            <GridItem :gridSpan="1">
              <Select
                :class="$style.select"
                :disabled="false"
                :isHelperTextMarkdown="false"
                :options="dropDownOptions"
                :value="dropDownValue"
                @input="handleDropDownChange"
              />
            </GridItem>
            <GridItem
              :class="$style.countItemsLabel"
              :gridSpan="1"
            >
              {{ totalCount[1] }}
            </GridItem>
          </Grid>
        </template>
      </ItemListPagination>
      <NoSearchResults
        v-if="showEmptyState"
        :ctaText="startOver"
        :instructions="startOver"
        :src="noResultsIllustration"
        @click="clearSearchTerm"
      />
    </Container>
  </div>
</template>

<script>
import {
  Constants,
  Container,
  Grid,
  GridItem,
  InputSearch,
  ItemListPagination,
  NoSearchResults,
  Select,
  Types,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import CommandTemplateManageRow from 'client/Commands/CommandTemplateManageRow.vue';
import CommandTypes from 'client/Commands/constants/CommandTypes';
import pluralize from 'pluralize';

const { Column } = Types;
const { alignment } = Constants;
const { commandCustomTemplate } = ItemTypeConsts;
const { linux, windows, mac } = CommandTypes;
const { noResultsIllustration, sortDirections } = Constants;

const sortDirectionDefault = sortDirections.ascending;
const sortByDefault = 'name';
const searchTermDefault = '';
const defaultDropDownOption = { name: 'All', value: 'all' };
const currentPageNumDefault = 0;
const rowsPerPageDefault = 50;

const commandCustomTemplateCollection = StoreModuleMap[commandCustomTemplate];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const commandTypeFilter = { name: 'commandType', operator: 'eq', value: '' };

export default {
  name: 'CommandTemplateManageItemTable',

  components: {
    Container,
    Grid,
    GridItem,
    InputSearch,
    ItemListPagination,
    NoSearchResults,
    Select,
  },

  props: {
    itemData: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isCollectionMapping: false,
      isListLoading: false,
      focusSearch: true,
      currentPageNum: currentPageNumDefault,
      rowsPerPage: rowsPerPageDefault,
      sortDirection: sortDirectionDefault,
      sortBy: sortByDefault,
      sortField: `${sortDirectionDefault === sortDirections.ascending ? '' : '-'}${sortByDefault}`,
      searchTerm: searchTermDefault,
      dropDownValue: defaultDropDownOption.value,
      noResultsIllustration,

      columns: [
        new Column({
          label: 'Template Name',
          dataFieldName: 'name',
          sortable: true,
          width: '2.5fr',
        }),
        new Column({
          label: 'Schedule',
          dataFieldName: 'schedule',
          sortable: false,
          width: '.8fr',
        }),
        new Column({
          label: 'Command',
          dataFieldName: 'command',
          sortable: false,
          width: '3fr',
        }),
        new Column({
          label: 'OS',
          dataFieldName: 'operatingSystem',
          sortable: false,
          width: '1fr',
        }),
        new Column({
          label: '',
          width: '2fr',
          alignment: alignment.right,
        }),
      ],

      dropDownOptions: [
        defaultDropDownOption,
        { name: this.formatDisplayValue(linux), value: linux },
        { name: this.formatDisplayValue(windows), value: windows },
        { name: this.formatDisplayValue(mac), value: mac },
      ],
    };
  },

  CommandTemplateManageRow,

  computed: {
    ...mapGetters('CommandCustomTemplateCollection', ['getCollection']),
    ...mapState('CommandCustomTemplateCollection', {
      stateCount: (state) => state.entities.items.totalCount,
    }),
    ...mapState(settingsModule, [
      'orgId',
      'user',
    ]),

    showEmptyState() {
      return !this.totalCount[0]
        && !this.isListLoading
        && !this.isCollectionMapping;
    },

    startOver() {
      return this.searchTerm ? undefined : '';
    },

    totalCount() {
      const label = `${this.stateCount} ${pluralize('item', this.stateCount)}`;
      return [this.stateCount, label];
    },
  },

  async created() {
    this.setProviderId(this.user.provider);
    await this.resetCollection();
    await this.refreshData();
  },
  methods: {
    ...mapActions(commandCustomTemplateCollection, [
      'fetchCollection',
      'setProviderId',
      'resetCollection',
      'setPage',
      'setSearchTerm',
      'setSortField',
      'setFilter',
      'setPage',
      'deleteFilter',
    ]),

    ...mapActions(notificationModule, [
      'triggerErrorNotification',
    ]),

    clearSearchTerm() {
      this.handleEnterSearchTerm('');
      this.focusSearch = true;
    },

    formatDisplayValue(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    async handleDropDownChange(value) {
      this.dropDownValue = value;
      this.currentPageNum = currentPageNumDefault;
      this.resetCollection({ soft: true });
      if (value === defaultDropDownOption.value) {
        this.deleteFilter({ filterName: commandTypeFilter.name });
      } else {
        const filter = { name: 'commandType', operator: 'eq', value };
        this.setFilter({ filter });
      }

      await this.refreshData();
    },

    async handleEnterSearchTerm(searchTerm) {
      if (searchTerm.length && !searchTerm.trim().length) return;
      this.searchTerm = searchTerm;
      this.shouldFocusSearch = false;
      this.currentPageNum = currentPageNumDefault;
      this.resetCollection({ soft: true });
      this.setSearchTerm({ searchTerm, preventReset: true });
      await this.refreshData();
    },

    async handlePageChange({ currentPageNum, rowsPerPage }) {
      this.currentPageNum = currentPageNum;
      this.rowsPerPage = rowsPerPage;
      this.isListLoading = true;
      await this.resetCollection();
      this.setPage({ pageNumber: currentPageNum, limit: rowsPerPage });
      await this.refreshData();
    },

    async handleSortColumn({ dataFieldName, sortDirection }) {
      this.sortDirection = sortDirection;
      this.sortBy = dataFieldName;
      this.sortField = `${this.sortDirection === sortDirections.ascending ? '-' : ''}${dataFieldName}`;
      this.resetCollection({ soft: true });
      this.setSortField({ sortField: this.sortField });
      await this.refreshData();
    },
    async refreshData() {
      this.isListLoading = true;
      try {
        await this.fetchCollection();
      } catch (error) {
        this.triggerErrorNotification({
          title: 'Error',
          message: 'There was a problem fetching the data.',
        });
        this.isListLoading = false;
      } finally {
        this.isListLoading = false;
      }
    },
  },
};
</script>

  <style module>

  .toolbar {
    align-items: center;
    display: flex;
  }

  .filterLabel {
    text-align: right;
  }

  .select {
    margin-top: 2em;
  }

  .container.container {
    margin-top: 0.5em;
    padding-top: 0.5em !important;
  }

  .countItemsLabel {
    color: var(--jc-color-disabled-text);
  }

  </style>
